import { useState, useEffect, useRef, useCallback } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition'
import { entryI18n } from '../i18n/visitorPortalI18n'
import { Helmet } from 'react-helmet-async'
import logo from "../assets/images/logo.svg";
import chitrapatImage from "../assets/images/chitrapat.png";
import { FlagIcon } from 'react-flag-kit'
/* ────────── V2 Color Palette (aligned with V4 theme) ────────── */

// Country dropdown + dial codes (same concept as VisitorForm.jsx)
const countryCodes = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'RU', name: 'Russia' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'PL', name: 'Poland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'FI', name: 'Finland' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'PT', name: 'Portugal' },
  { code: 'GR', name: 'Greece' },
  { code: 'TR', name: 'Turkey' },
  { code: 'IR', name: 'Iran' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'TH', name: 'Thailand' },
  { code: 'SG', name: 'Singapore' },
]

const dialCodes = {
  US: '+1',
  CA: '+1',
  GB: '+44',
  AU: '+61',
  DE: '+49',
  FR: '+33',
  IT: '+39',
  ES: '+34',
  JP: '+81',
  CN: '+86',
  IN: '+91',
  BR: '+55',
  MX: '+52',
  AR: '+54',
  CL: '+56',
  CO: '+57',
  PE: '+51',
  ZA: '+27',
  NG: '+234',
  KE: '+254',
  EG: '+20',
  SA: '+966',
  AE: '+971',
  RU: '+7',
  UA: '+380',
  PL: '+48',
  SE: '+46',
  FI: '+358',
  NO: '+47',
  DK: '+45',
  NL: '+31',
  BE: '+32',
  PT: '+351',
  GR: '+30',
  TR: '+90',
  IR: '+98',
  PK: '+92',
  BD: '+880',
  TH: '+66',
  SG: '+65',
  MY: '+60',
}

const V2 = {
  accent: '#D4622B',
  accentLight: '#E8845A',
  accentDark: '#B84F1F',
  accentBg: 'rgba(212,98,43,0.08)',
  accentBg2: '#F2F0EB',
  cardBg: '#FFFFFF',
  border: 'rgba(62,42,28,0.08)',
  textDark: '#2C1E14',
  textMuted: '#7A6B5E',
  textLight: '#B0A498',
  bg1: '#F2F0EB',
  bg2: '#F2F0EB',
  bg3: '#F2F0EB',
  success: '#5A7A6B',
  gradient: 'linear-gradient(135deg, #E8845A, #D4622B)',
  gradientSoft: 'linear-gradient(180deg, #F2F0EB 0%, #F2F0EB 30%, #F2F0EB 100%)',
  headerGrad: 'linear-gradient(180deg, #F2F0EB 0%, #F2F0EB 35%, #F2F0EB 70%, #F2F0EB 100%)',
  shadow: '0 1px 4px rgba(62,42,28,0.04), 0 6px 20px rgba(62,42,28,0.05)',
  shadowMd: '0 2px 8px rgba(62,42,28,0.06), 0 12px 32px rgba(62,42,28,0.07)',
  shadowGlow: '0 4px 20px rgba(212,98,43,0.25)',
}

/* ────────── i18n strings ────────── */
const i18n = entryI18n

// NOTE: previously City was a dropdown. Now it's a free-text input.
// Keeping this here commented for reference if dropdown is needed again.
// const cities = [
//   'Ahmedabad', 'Mumbai', 'Delhi', 'Bangalore', 'Chennai',
//   'Pune', 'Hyderabad', 'Kolkata', 'Jaipur', 'Surat',
//   'Vadodara', 'Rajkot', 'Lucknow', 'Indore', 'Bhopal',
// ]

/* ────────── Lotus SVG (taupe version) ────────── */
const LotusSVG = ({ size = 80, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="50" cy="42" rx="10" ry="28" fill={V2.accent} opacity="0.9" />
    <ellipse cx="50" cy="42" rx="10" ry="26" fill={V2.accentLight} opacity="0.7" transform="rotate(-30 50 50)" />
    <ellipse cx="50" cy="42" rx="10" ry="24" fill={V2.accentLight} opacity="0.5" transform="rotate(-60 50 50)" />
    <ellipse cx="50" cy="42" rx="10" ry="26" fill={V2.accentLight} opacity="0.7" transform="rotate(30 50 50)" />
    <ellipse cx="50" cy="42" rx="10" ry="24" fill={V2.accentLight} opacity="0.5" transform="rotate(60 50 50)" />
    <ellipse cx="50" cy="42" rx="8" ry="22" fill={V2.accentLight} opacity="0.35" transform="rotate(-90 50 50)" />
    <ellipse cx="50" cy="42" rx="8" ry="22" fill={V2.accentLight} opacity="0.35" transform="rotate(90 50 50)" />
    <circle cx="50" cy="50" r="5" fill={V2.accentDark} opacity="0.8" />
  </svg>
)

/* ────────── Main Component ────────── */
const EntryNewOne = () => {
  const [lang, setLang] = useState('en')
  const t = i18n[lang]

  const { transcript, resetTranscript } = useSpeechRecognition()
  const [showVoice, setShowVoice] = useState(true)
  const [listeningVisitorId, setListeningVisitorId] = useState(null)

  const emptyVisitor = () => ({
    id: Date.now() + Math.random(),
    mobile: '',
    fullName: '',
    country: 'IN',
    dialCode: '+91',
    gender: '',
    age: '',
    expanded: true,
  })

  const [visitDate, setVisitDate] = useState(new Date().toISOString().split('T')[0])
  const [city, setCity] = useState('')
  const [visitors, setVisitors] = useState([emptyVisitor()])
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [duplicateNameIds, setDuplicateNameIds] = useState([])
  const formRef = useRef(null)

  /* ── auto-save to sessionStorage ── */
  const saveKey = 'ashram_day_visit_v2_draft'
  useEffect(() => {
    const saved = sessionStorage.getItem(saveKey)
    if (saved) {
      try {
        const data = JSON.parse(saved)
        if (data.visitDate) setVisitDate(data.visitDate)
        if (data.city) setCity(data.city)
        if (data.visitors?.length) setVisitors(data.visitors)
        if (data.lang) setLang(data.lang)
      } catch { /* ignore */ }
    }
  }, [])

  useEffect(() => {
    if (!submitted) {
      sessionStorage.setItem(saveKey, JSON.stringify({ visitDate, city, visitors, lang }))
    }
  }, [visitDate, city, visitors, lang, submitted])

  useEffect(() => {
    const nameMap = new Map()
    const nameDupes = new Set()

    // Mobile numbers are intentionally NOT checked for duplicates — the same
    // number may be shared by every guest in a group.
    visitors.forEach(visitor => {
      const nameKey = visitor.fullName.trim().toLowerCase()

      if (nameKey) {
        if (nameMap.has(nameKey)) {
          nameDupes.add(visitor.id)
          nameDupes.add(nameMap.get(nameKey))
        } else {
          nameMap.set(nameKey, visitor.id)
        }
      }
    })

    setDuplicateNameIds(Array.from(nameDupes))
  }, [visitors])

  const updateVisitor = useCallback((id, field, value) => {
    setVisitors(prev => prev.map(v => v.id === id ? { ...v, [field]: value } : v))
  }, [])

  const handleVoiceInput = useCallback((visitorId) => {
    if (!showVoice) return

    if (listeningVisitorId === visitorId) {
      SpeechRecognition.stopListening()
      setListeningVisitorId(null)
      return
    }

    resetTranscript()
    SpeechRecognition.startListening({ continuous: true })
    setListeningVisitorId(visitorId)
  }, [listeningVisitorId, resetTranscript, showVoice])

  useEffect(() => {
    if (!SpeechRecognition.browserSupportsSpeechRecognition()) {
      setShowVoice(false)
    }
  }, [])

  useEffect(() => {
    if (!listeningVisitorId) return
    updateVisitor(listeningVisitorId, 'fullName', transcript)
  }, [transcript, listeningVisitorId, updateVisitor])

  useEffect(() => {
    return () => {
      SpeechRecognition.stopListening()
    }
  }, [])

  const toggleVisitorExpand = (id) => {
    setVisitors(prev => prev.map(v => v.id === id ? { ...v, expanded: !v.expanded } : v))
  }

  const addVisitor = () => {
    setVisitors(prev => [...prev, emptyVisitor()])
  }

  const removeVisitor = (id) => {
    setVisitors(prev => prev.filter(v => v.id !== id))
  }

  // A mobile number is optional per visitor, but when typed it must be valid.
  const hasValidMobile = (v) => (v.mobile || '').replace(/\D/g, '').length >= 10
  const hasInvalidMobile = (v) => {
    const digits = (v.mobile || '').replace(/\D/g, '')
    return digits.length > 0 && digits.length < 10
  }

  // First visitor who typed a number — used for anyone who left the field blank.
  const primaryContact = visitors.find(hasValidMobile)

  const isValid = () => {
    if (!visitDate || !city) return false
    if (duplicateNameIds.length) return false
    if (visitors.some(hasInvalidMobile)) return false
    // At least one number for the whole group so the visit stays reachable.
    if (!primaryContact) return false

    return visitors.every(v => v.fullName.trim() && v.gender)
  }

  const handleSubmit = async () => {
    if (!isValid()) {

      setShowErrors(true)
      if (!primaryContact && !visitors.some(hasInvalidMobile)) {
        toast.error(t.mobileAtLeastOne)
      }
      return
    }

    if (duplicateNameIds.length) {
      toast.error(t.nameMustBeUnique)
      return
    }

    const payload = visitors.map(visitor => {
      const ownMobile = (visitor.mobile || '').toString().replace(/\D/g, '')
      // Blank numbers fall back to the group's first number — duplicates are allowed.
      const useContact = ownMobile.length >= 10 ? visitor : primaryContact
      const mobile = (useContact.mobile || '').toString().replace(/\D/g, '')
      const dial = (useContact.dialCode || '+91').toString()
      const dialDigits = dial.replace(/^\+/, '')
      return {
        FullName: visitor.fullName,
        // Backend APIs (getUserFromMobno) return CountryDialingCode as digits (e.g. 91),
        // so we send digits to stay consistent.
        CountryDialingCode: dialDigits,
        MobileNo: mobile,
        VisitorType: 'V',
        Age: parseInt(visitor.age, 10) || 0,
        Gender: visitor.gender.charAt(0).toUpperCase(),
        City: city,
      }
    })

    try {
      setIsSubmitting(true)
      const response = await fetch(
        `${process.env.REACT_APP_API_VMS_URL}/saveMultipleVisitors`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      )

      if (!response.ok) {
        throw new Error('Network response was not ok ' + response.statusText)
      }

      const result = await response.json()

      if (result.isSuccess) {
        toast.success(`${t.successToastPrefix} ${result.message}`)
        setTimeout(() => {
          setIsSubmitting(false)
          window.location.href = result?.data?.returnUrl
        }, 2000)
      } else {
        toast.error(`${t.errorToastPrefix} ${result.message}`)
        setIsSubmitting(false)
      }
    } catch (error) {
      toast.error(`${t.errorToastPrefix} ${error.message}`)
      setIsSubmitting(false)
    }
  }

  const today = new Date().toISOString().split('T')[0]

  /* ── inline styles using V2 palette ── */
  const inputStyle = {
    width: '100%',
    borderRadius: '16px',
    border: `2px solid ${V2.border}`,
    background: V2.cardBg,
    padding: '14px 16px',
    color: V2.textDark,
    fontSize: '16px',
    fontFamily: "'Inter', sans-serif",
    outline: 'none',
    transition: 'border-color 0.3s, box-shadow 0.3s',
  }
  const inputFocusHandler = (e) => {
    e.target.style.borderColor = V2.accent
    e.target.style.boxShadow = `0 0 0 3px ${V2.accent}1F`
  }
  const inputBlurHandler = (e) => {
    e.target.style.borderColor = V2.border
    e.target.style.boxShadow = 'none'
  }



  /* ────────── SUCCESS SCREEN ────────── */
  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12" style={{ background: V2.gradientSoft }}>
        <div className="animate-bloom">
          <LotusSVG size={100} className="lotus-bloom mx-auto mb-6" />
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-center mb-3 animate-fade-in" style={{ color: V2.textDark }}>
          {t.successTitle}
        </h1>
        <p className="text-center text-sm sm:text-base mb-8 max-w-xs animate-fade-in" style={{ color: V2.textMuted }}>
          {t.successSubtitle}
        </p>

        <div className="w-full max-w-sm rounded-3xl p-6 mb-8 animate-card-enter" style={{ background: V2.cardBg, boxShadow: V2.shadowMd, border: `1px solid ${V2.border}` }}>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: V2.accent }}>{t.entryPass}</span>
            <span className="text-xs" style={{ color: V2.textMuted }}>{visitDate}</span>
          </div>
          <div className="space-y-3">
            {visitors.map((v, i) => (
              <div key={v.id} className="flex items-center gap-3 py-2" style={{ borderBottom: i < visitors.length - 1 ? `1px solid ${V2.border}` : 'none' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold" style={{ background: V2.accentBg, color: V2.accent }}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: V2.textDark }}>{v.fullName}</div>
                  <div className="text-xs" style={{ color: V2.textMuted }}>{v.gender}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 flex items-center justify-between" style={{ borderTop: `1px solid ${V2.border}` }}>
            <span className="text-xs" style={{ color: V2.textMuted }}>{city}</span>
            <span className="text-xs" style={{ color: V2.textMuted }}>{visitors.length} {t.visitors}</span>
          </div>
        </div>

        <button
          onClick={() => {
            setSubmitted(false); setVisitors([emptyVisitor()]); setVisitDate(new Date().toISOString().split('T')[0]); setCity(''); setShowErrors(false)
          }}
          className="btn-ripple px-8 py-3.5 rounded-full font-semibold text-white text-base active:scale-95 transition-transform"
          style={{ background: V2.gradient, boxShadow: V2.shadowGlow }}
        >
          {t.successBtn}
        </button>
        <ToastContainer />
      </div>
    )
  }

  /* ────────── MAIN FORM ────────── */
  return (
    <div className="min-h-screen pb-28" style={{ background: V2.gradientSoft }}>
      {/* ── Sticky Language Toggle ── */}
      <div className="sticky top-0 z-50 backdrop-blur-md" style={{ background: `${V2.bg1}CC`, borderBottom: `1px solid ${V2.border}80` }}>
        <div className="max-w-lg mx-auto px-4 py-2.5 flex items-center justify-center">
          <div className="inline-flex rounded-full p-1 gap-0.5" style={{ background: `${V2.accentBg2}CC` }}>
            {[
              { code: 'en', label: 'English' },
              { code: 'gu', label: 'ગુજરાતી' },
              { code: 'hi', label: 'हिंदी' },
            ].map(l => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className="px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all touch-manipulation"
                style={{
                  background: lang === l.code ? V2.cardBg : 'transparent',
                  color: lang === l.code ? V2.accent : V2.textMuted,
                  boxShadow: lang === l.code ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
                }}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <Helmet>
        <title>Shrimad Rajchandra Mission Dharampur - Entry</title>{" "}
        {/* Set specific title for EPassPage */}
      </Helmet>

      {/* ── Divine Header ── */}
      <header className="relative overflow-hidden animate-fade-in" style={{ background: V2.headerGrad, minHeight: '55vh' }}>
        {/* Mandala watermark */}
        <div className="mandala-header" style={{ opacity: 0.03 }}>
          <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="100" cy="100" r="95" stroke={V2.accent} strokeWidth="0.5" />
            <circle cx="100" cy="100" r="80" stroke={V2.accent} strokeWidth="0.4" />
            <circle cx="100" cy="100" r="60" stroke={V2.accent} strokeWidth="0.3" />
            <circle cx="100" cy="100" r="40" stroke={V2.accent} strokeWidth="0.3" />
            <circle cx="100" cy="100" r="20" stroke={V2.accent} strokeWidth="0.2" />
            <path d="M100 5 L100 195 M5 100 L195 100" stroke={V2.accent} strokeWidth="0.3" />
            <path d="M15 15 L185 185 M185 15 L15 185" stroke={V2.accent} strokeWidth="0.2" />
            <ellipse cx="100" cy="100" rx="90" ry="45" stroke={V2.accent} strokeWidth="0.3" />
            <ellipse cx="100" cy="100" rx="45" ry="90" stroke={V2.accent} strokeWidth="0.3" />
            <ellipse cx="100" cy="100" rx="80" ry="40" stroke={V2.accent} strokeWidth="0.2" transform="rotate(45 100 100)" />
            <ellipse cx="100" cy="100" rx="80" ry="40" stroke={V2.accent} strokeWidth="0.2" transform="rotate(-45 100 100)" />
          </svg>
        </div>

        {/* Soft ambient orbs — muted taupe tones */}
        <div className="absolute top-[-40px] left-1/2 -translate-x-1/2 w-[300px] h-[200px] rounded-full blur-3xl pointer-events-none" style={{ background: `${V2.accentLight}20` }} />
        <div className="absolute bottom-10 left-[-40px] w-[160px] h-[160px] rounded-full blur-3xl pointer-events-none" style={{ background: `${V2.accentBg2}40` }} />
        <div className="absolute bottom-10 right-[-40px] w-[160px] h-[160px] rounded-full blur-3xl pointer-events-none" style={{ background: `${V2.accentBg2}40` }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center pt-6 pb-16 sm:pt-8 sm:pb-20 px-6">
          {/* Logo */}
          <div className="mb-6 sm:mb-8 animate-fade-in">
            <img src={logo} alt="Ashram Logo" className="ashram-logo" />
          </div>

          {/* Divine portrait */}
          <div className="relative mb-6 sm:mb-8">
            {/* Aura glow — taupe */}
            <div
              className="absolute top-1/2 left-1/2 w-[200px] h-[200px] rounded-full pointer-events-none"
              style={{
                transform: 'translate(-50%, -50%)',
                background: `radial-gradient(circle, ${V2.accentLight}30 0%, ${V2.accent}12 35%, ${V2.accentBg2}08 60%, transparent 80%)`,
                filter: 'blur(30px)',
                animation: 'auraBreath 5s ease-in-out infinite',
              }}
            />
            {/* Halo ring — taupe */}
            <div
              className="absolute top-1/2 left-1/2 w-[150px] h-[150px] rounded-full pointer-events-none"
              style={{
                transform: 'translate(-50%, -50%)',
                border: `1.5px solid ${V2.accentLight}30`,
                animation: 'haloRotate 20s linear infinite',
              }}
            />
            <img
              src={chitrapatImage}
              alt="Divine Portrait"
              className="divine-portrait"
              style={{
                borderColor: `${V2.accentLight}40`,
                background: `radial-gradient(circle, ${V2.bg1} 60%, ${V2.accentBg} 100%)`,
                boxShadow: `0 4px 30px ${V2.accent}15, 0 0 60px ${V2.accentLight}10`,
              }}
            />
            {/* Vignette */}
            <div
              className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-[150px] h-[80px] pointer-events-none"
              style={{
                background: `linear-gradient(to top, ${V2.accentBg} 10%, ${V2.accentBg}80 50%, transparent 100%)`,
                borderRadius: '0 0 50% 50%',
                zIndex: 11,
              }}
            />
          </div>

          {/* Title */}
          <div className="text-center">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] mb-2" style={{ color: V2.accent }}>
              ✦ {t.titleTag} ✦
            </p>
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mb-1" style={{ color: V2.textDark }}>
              {t.titleLine1}
            </h1>
            {t.titleLine2 && (
              <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-medium italic mb-3" style={{ color: V2.accent }}>
                {t.titleLine2}
              </h1>
            )}
            {/* Decorative divider */}
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="block w-8 h-px" style={{ background: `${V2.accentLight}60` }} />
              <span className="text-xs" style={{ color: V2.accentLight }}>🙏</span>
              <span className="block w-8 h-px" style={{ background: `${V2.accentLight}60` }} />
            </div>
            <p className="text-sm sm:text-base max-w-xs mx-auto" style={{ color: V2.textMuted }}>
              {t.subtitle}
            </p>
          </div>
        </div>

        {/* Curved divider */}
        <div className="curved-divider">
          <svg viewBox="0 0 1440 40" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 40 L0 10 Q360 0 720 10 Q1080 20 1440 10 L1440 40 Z" fill={V2.bg1} />
          </svg>
        </div>
      </header>

      {/* ── Form Body ── */}
      <main ref={formRef} className="max-w-lg mx-auto px-4 -mt-4 relative z-30 space-y-5">

        {/* ╌╌╌ Section 1: Visit Details ╌╌╌ */}
        <section className="rounded-3xl p-5 sm:p-6 animate-card-enter" style={{ background: V2.cardBg, boxShadow: V2.shadow, border: `1px solid ${V2.border}` }}>
          <h2 className="font-heading text-lg sm:text-xl font-semibold mb-5" style={{ color: V2.textDark }}>
            {t.visitDetails}
          </h2>

          {/* Date */}
          <label className="block mb-4 ">
            <span className="text-xs font-semibold  uppercase tracking-wider mb-1.5 block" style={{ color: V2.textMuted }}>
              {t.dateLabel}
            </span>
            <input
              type="date"
              value={visitDate}
              min={today}
              disabled={true}
              style={{
                ...inputStyle,
                backgroundColor: 'rgb(238 238 238)', // gray color
                color: '#000',
                borderColor: showErrors && !visitDate ? '#f87171' : V2.border,
                cursor: 'not-allowed'
              }}
              onChange={e => setVisitDate(e.target.value)}

              onFocus={inputFocusHandler}
              onBlur={inputBlurHandler}
            />
            {showErrors && !visitDate && <span className="text-xs text-red-500 mt-1 block">{t.required}</span>}
          </label>

          {/* City */}
          <label className="block mb-4">
            <span className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: V2.textMuted }}>
              {t.cityLabel}
            </span>
            <input
              type="text"
              value={city}
              onChange={e => setCity(e.target.value)}
              placeholder={t.cityPlaceholder}
              style={{
                ...inputStyle,
                color: city ? V2.textDark : V2.textLight,
                borderColor: showErrors && !city ? '#f87171' : V2.border,
              }}
              onFocus={inputFocusHandler}
              onBlur={inputBlurHandler}
            />
            {showErrors && !city && <span className="text-xs text-red-500 mt-1 block">{t.required}</span>}
          </label>

          {/* Visitor count stepper */}
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider mb-2 block" style={{ color: V2.textMuted }}>
              {t.visitors}
            </span>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => visitors.length > 1 && removeVisitor(visitors[visitors.length - 1].id)}
                disabled={visitors.length <= 1}
                className="stepper-btn disabled:opacity-30"
                style={{ background: V2.accentBg2, color: V2.textDark }}
              >
                −
              </button>
              <span className="text-2xl font-semibold min-w-[2ch] text-center font-heading" style={{ color: V2.textDark }}>
                {visitors.length}
              </span>
              <button
                type="button"
                onClick={addVisitor}
                disabled={visitors.length >= 10}
                className="stepper-btn disabled:opacity-30"
                style={{ background: V2.accentBg, color: V2.accent }}
              >
                +
              </button>
            </div>
          </div>
        </section>

        {/* ╌╌╌ Section 2: Visitor Details ╌╌╌ */}
        <section className="space-y-4">
          <h2 className="font-heading text-lg sm:text-xl font-semibold px-1" style={{ color: V2.textDark }}>
            {t.visitorDetails}
          </h2>

          {visitors.map((visitor, idx) => (
            <V2VisitorCard
              key={visitor.id}
              visitor={visitor}
              index={idx}
              total={visitors.length}
              t={t}
              showErrors={showErrors}
              duplicateNameIds={duplicateNameIds}
              onUpdate={updateVisitor}
              showVoice={showVoice}
              isListening={listeningVisitorId === visitor.id}
              onVoiceInput={() => handleVoiceInput(visitor.id)}
              onToggle={toggleVisitorExpand}
              onRemove={removeVisitor}
              inputStyle={inputStyle}
              inputFocusHandler={inputFocusHandler}
              inputBlurHandler={inputBlurHandler}
            />
          ))}

          {visitors.length < 10 && (
            <button
              type="button"
              onClick={addVisitor}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-medium text-sm active:scale-[0.98] transition-all touch-manipulation"
              style={{ border: `2px dashed ${V2.accentBg2}`, color: V2.accent, background: 'transparent' }}
              onMouseEnter={e => e.currentTarget.style.background = `${V2.accentBg}40`}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span className="text-lg leading-none">+</span>
              {t.addVisitor}
            </button>
          )}
        </section>

        <div className="h-4" />
      </main>

      {/* ── Sticky Bottom CTA ── */}
      <div className="fixed bottom-0 inset-x-0 z-50 pt-4 pb-6 px-4" style={{ background: `linear-gradient(to top, ${V2.bg1}, ${V2.bg1}F2, transparent)` }}>
        <div className="max-w-lg mx-auto">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="btn-ripple w-full py-4 rounded-full font-semibold text-white text-base sm:text-lg active:scale-[0.97] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: V2.gradient, boxShadow: V2.shadowGlow }}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t.ctaContinue}...
              </span>
            ) : t.cta}
          </button>
        </div>
      </div>
      <ToastContainer />
    </div>
  )
}

/* ────────── V2 Visitor Card ────────── */
const V2VisitorCard = ({ visitor, index, total, t, showErrors, duplicateNameIds, onUpdate, showVoice, isListening, onVoiceInput, onToggle, onRemove, inputStyle, inputFocusHandler, inputBlurHandler }) => {
  const isDuplicateName = duplicateNameIds.includes(visitor.id)
  // Mobile is optional; only a half-typed number counts as an error.
  const mobileDigits = (visitor.mobile || '').replace(/\D/g, '')
  const isInvalidMobile = mobileDigits.length > 0 && mobileDigits.length < 10
  const isMissingName = !visitor.fullName.trim()
  const [memberDetails, setMemberDetails] = useState(null)
  const [showDropdown, setShowDropdown] = useState(false)

  const selectedCountry = visitor.country || 'IN'
  const selectedDialCode = visitor.dialCode || dialCodes[selectedCountry] || '+91'

  const toggleDropdown = (e) => {
    // prevent card toggle when interacting with dropdown
    e?.stopPropagation?.()
    setShowDropdown(prev => !prev)
  }

  const handleCountryCodeChange = (e) => {
    const nextCountry = e.target.value
    const nextDial = dialCodes[nextCountry] || '+91'
    onUpdate(visitor.id, 'country', nextCountry)
    onUpdate(visitor.id, 'dialCode', nextDial)
    setShowDropdown(false)
  }

  const hasError = (field) => {
    if (!showErrors) return false
    if (field === 'mobile') return isInvalidMobile
    if (field === 'fullName') return isMissingName || isDuplicateName
    if (field === 'gender') return !visitor.gender
    return false
  }

  const findCountryCodeFromDialDigits = (countryDialingCode) => {
    // API returns digits (e.g. 91, 60). We map it back to our country code
    // so the FlagIcon + dial code UI updates just like VisitorForm.jsx.
    const dialDigits = (countryDialingCode ?? '').toString().trim()
    if (!dialDigits) return null

    const dialWithPlus = `+${dialDigits}`
    let matched = null
    Object.keys(dialCodes).some((code) => {
      if (dialWithPlus.startsWith(dialCodes[code])) {
        matched = code
        return true
      }
      return false
    })

    return matched
  }

  const DetailsFromMobile = async (mobile) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_VMS_URL}/getUserFromMobno?mobileno=${mobile}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })
      const result = await response.json()
      if (Array.isArray(result?.data) && result.data[0]) return result.data[0]

      return null
    } catch (error) {
      console.log(error)
      return null
    }
  }

  const normalizeGenderFromApi = (apiGender) => {
    const g = (apiGender || '').toString().trim().toLowerCase()
    if (!g) return ''

    // API may return: "M"/"F"/"O" or "Male"/"Female"/"Other"
    if (g === 'm' || g === 'male') return t.male
    if (g === 'f' || g === 'female') return t.female
    if (g === 'o' || g === 'other') return t.other

    // fallback: if backend already returns localized/expected value
    return apiGender
  }


  const handleMobileBlur = async (visitor) => {
    const mobile = (visitor.mobile || '').replace(/\D/g, '')
    if (!mobile || mobile.length < 10) return

    const details = await DetailsFromMobile(mobile)
    if (!details) return

    // Only update if we actually got something meaningful
    if (!(details.FirstName && details.LastName && details.MobileNo && details.Age)) return

    setMemberDetails(details)

    // Auto-select country + dial code for flag display
    const countryCode = findCountryCodeFromDialDigits(details.CountryDialingCode)
    if (countryCode) {
      onUpdate(visitor.id, 'country', countryCode)
      onUpdate(visitor.id, 'dialCode', dialCodes[countryCode] || '+91')
    }

    const fullName = `${details.FirstName || ''}${details.LastName ? ' ' + details.LastName : ''}`.trim()
    if (fullName) onUpdate(visitor.id, 'fullName', fullName)
    if (details.Age) onUpdate(visitor.id, 'age', parseInt(details.Age, 10))
    if (details.Gender) onUpdate(visitor.id, 'gender', normalizeGenderFromApi(details.Gender))
    if (details.MobileNo) onUpdate(visitor.id, 'mobile', details.MobileNo.toString().replace(/\D/g, '').slice(-10))
  }

  const maskName = (name) => {
    if (!name) return ''
    const trimmed = name.toString().replace(/\s+/g, '')
    const starCount = Math.max(0, trimmed.length - 2)
    const masked = trimmed.replace(/(.).*(.)$/, `$1${'*'.repeat(starCount)}$2`)
    return masked.charAt(0).toUpperCase() + masked.slice(1)
  }

  return (
    <div
      className="rounded-3xl overflow-hidden animate-card-enter"
      style={{ background: V2.cardBg, boxShadow: V2.shadow, border: `1px solid ${V2.border}`, animationDelay: `${index * 80}ms` }}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => onToggle(visitor.id)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 touch-manipulation transition-colors"
        style={{ background: 'transparent' }}
        onMouseEnter={e => e.currentTarget.style.background = V2.accentBg + '40'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0" style={{ background: V2.accentBg, color: V2.accent }}>
            {index + 1}
          </div>
          <div className="text-left">
            <span className="text-sm font-semibold block" style={{ color: V2.textDark }}>
              {visitor.fullName || `${t.visitor} ${index + 1}`}
            </span>
            {!visitor.expanded && visitor.fullName && visitor.gender && (
              <span className="text-xs" style={{ color: V2.textMuted }}>
                {visitor.gender}{visitor.age ? ` · ${t.ageValue(visitor.age)}` : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {total > 1 && (
            <span
              onClick={(e) => { e.stopPropagation(); onRemove(visitor.id) }}
              className="text-xs font-medium px-2 py-1 rounded-lg transition-colors"
              style={{ color: '#ef4444' }}
            >
              {t.removeVisitor}
            </span>
          )}
          <svg
            className={`w-4 h-4 transition-transform duration-300 ${visitor.expanded ? 'rotate-180' : ''}`}
            fill="none" stroke={V2.textLight} viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Body */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-in-out"
        style={{ gridTemplateRows: visitor.expanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden min-h-0">
          <div className="px-5 pb-5 pt-1 space-y-4">

            {/* Mobile */}
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: V2.textMuted }}>
                {t.mobile}
              </span>
              <div className="flex gap-2">
                {/* Dropdown (same idea as VisitorForm.jsx) */}
                {showDropdown && (
                  <select
                    value={selectedCountry}
                    onChange={handleCountryCodeChange}
                    className="px-3 py-2 rounded-xl text-sm"
                    style={{
                      border: `2px solid ${V2.border}`,
                      background: V2.cardBg,
                      color: V2.textDark,
                    }}
                  >
                    {countryCodes.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}

                <div
                  className="flex items-center gap-1 px-3 py-3.5 rounded-2xl text-sm flex-shrink-0"
                  style={{ border: `2px solid ${V2.border}`, background: V2.accentBg, color: V2.textMuted }}
                  onClick={toggleDropdown}
                >
                  <div className="relative p-1">
                    <FlagIcon code={selectedCountry} size={30} />
                  </div>
                  <span className="font-medium">{selectedDialCode}</span>
                </div>

                {
                  !showDropdown && (
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={visitor.mobile}
                      onChange={e => {
                        // If user edits mobile after a member was found, show editable fields again
                        if (memberDetails) setMemberDetails(null)
                        onUpdate(visitor.id, 'mobile', e.target.value.replace(/\D/g, ''))
                      }}

                      style={{ ...inputStyle, flex: 1, borderColor: hasError('mobile') ? '#f87171' : V2.border }}
                      onFocus={inputFocusHandler}
                      onBlur={(e) => {
                        inputBlurHandler(e)
                        handleMobileBlur(visitor)
                      }}
                    />
                  )
                }


              </div>
              {showErrors && isInvalidMobile && <span className="text-xs text-red-500 mt-1 block">{t.mobileInvalid}</span>}
            </label>

            {memberDetails?.FirstName && memberDetails?.Gender && memberDetails?.Age && (
              <div className="rounded-2xl p-4" style={{ border: `1px solid ${V2.border}`, background: V2.accentBg2 }}>
                <div className="flex items-center justify-center mb-3">
                  <div className="text-sm font-semibold" style={{ color: V2.accent }}>
                    {t.memberFound}
                  </div>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: V2.textDark }}>
                      {maskName(memberDetails.FirstName)} {maskName(memberDetails.LastName)} · {memberDetails.Gender} · {memberDetails.Age}
                    </div>
                    {memberDetails.MobileNo && (
                      <div className="mt-2 flex items-center gap-2 text-sm" style={{ color: V2.accent }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                          <path
                            fill={V2.accent}
                            d="M9.366 10.682a10.56 10.56 0 0 0 3.952 3.952l.884-1.238a1 1 0 0 1 1.294-.296a11.4 11.4 0 0 0 4.583 1.364a1 1 0 0 1 .921.997v4.462a1 1 0 0 1-.898.995Q19.307 21 18.5 21C9.94 21 3 14.06 3 5.5q0-.807.082-1.602A1 1 0 0 1 4.077 3h4.462a1 1 0 0 1 .997.921A11.4 11.4 0 0 0 10.9 8.504a1 1 0 0 1-.296 1.294zm-2.522-.657l1.9-1.357A13.4 13.4 0 0 1 7.647 5H5.01Q5 5.25 5 5.5C5 12.956 11.044 19 18.5 19q.25 0 .5-.01v-2.637a13.4 13.4 0 0 1-3.668-1.097l-1.357 1.9a12.5 12.5 0 0 1-1.588-.75l-.058-.033a12.56 12.56 0 0 1-4.702-4.702l-.033-.058a12.4 12.4 0 0 1-.75-1.588"
                          />
                        </svg>
                        <span>{selectedDialCode} {memberDetails.MobileNo}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!memberDetails?.FirstName && !memberDetails?.Gender && !memberDetails?.Age && (
              <>
                {/* Full name */}
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: V2.textMuted }}>
                    {t.fullName}
                  </span>
                  <div className="relative">
                    <input
                      type="text"
                      value={visitor.fullName}
                      onChange={e => onUpdate(visitor.id, 'fullName', e.target.value)}
                      placeholder={t.fullName}
                      style={{
                        ...inputStyle,
                        paddingRight: showVoice ? '52px' : undefined,
                        borderColor: hasError('fullName') ? '#f87171' : V2.border,
                      }}
                      onFocus={inputFocusHandler}
                      onBlur={inputBlurHandler}
                    />
                    {showVoice && (
                      <button
                        type="button"
                        onClick={onVoiceInput}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full"
                        style={{ background: V2.cardBg }}
                        aria-label="Voice input"
                      >
                        {isListening ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 48 48" className="h-6 w-6">
                            <g fill="none" stroke={V2.accent} strokeWidth="4">
                              <path d="M24 44c11.046 0 20-8.954 20-20S35.046 4 24 4S4 12.954 4 24s8.954 20 20 20Z" />
                              <path strokeLinecap="round" d="M30 18v12m6-8v4m-18-8v12m-6-8v4m12-12v20" />
                            </g>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" className="h-6 w-6">
                            <path
                              fill={V2.accent}
                              d="M12 13q-.846 0-1.423-.577T10 11V5q0-.846.577-1.423T12 3t1.423.577T14 5v6q0 .846-.577 1.423T12 13m-.5 7.5v-3.517q-2.35-.216-3.925-1.922T6 11h1q0 2.075 1.463 3.538T12 16t3.538-1.463T17 11h1q0 2.356-1.575 4.062t-3.925 1.92V20.5z"
                            />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                  {showErrors && isMissingName && <span className="text-xs text-red-500 mt-1 block">{t.required}</span>}
                  {showErrors && isDuplicateName && <span className="text-xs text-red-500 mt-1 block">{t.nameMustBeUnique}</span>}
                </label>

                {/* Gender pills */}
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider mb-2 block" style={{ color: V2.textMuted }}>
                    {t.gender}
                  </span>
                  <div className="flex gap-2 flex-wrap">
                    {['male', 'female', 'other'].map(g => {
                      const isActive = visitor.gender === t[g]
                      return (
                        <button
                          key={g}
                          type="button"
                          onClick={() => onUpdate(visitor.id, 'gender', t[g])}
                          className="px-5 py-2.5 rounded-full text-sm font-medium cursor-pointer select-none touch-manipulation transition-all"
                          style={{
                            border: `2px solid ${isActive ? V2.accent : (hasError('gender') ? '#fca5a5' : V2.border)}`,
                            background: isActive ? `linear-gradient(135deg, ${V2.accentBg}, ${V2.accentBg2})` : V2.cardBg,
                            color: isActive ? V2.accentDark : V2.textMuted,
                          }}
                        >
                          {t[g]}
                        </button>
                      )
                    })}
                  </div>
                  {hasError('gender') && <span className="text-xs text-red-500 mt-1 block">{t.required}</span>}
                </div>

                {/* Age stepper */}
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider mb-2 block" style={{ color: V2.textMuted }}>
                    {t.age}
                  </span>
                  <div className="flex items-center gap-4">
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={120}
                      value={visitor.age}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          onUpdate(visitor.id, 'age', '');
                          return;
                        }
                        const num = Number(raw);
                        if (Number.isNaN(num)) return;
                        const clamped = Math.max(1, Math.min(120, num));
                        onUpdate(visitor.id, 'age', clamped);
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '') {
                          onUpdate(visitor.id, 'age', '');
                          return;
                        }
                        const num = Number(e.target.value);
                        const clamped = Math.max(1, Math.min(120, Number.isNaN(num) ? '' : num));
                        onUpdate(visitor.id, 'age', clamped);
                      }}
                      className="text-xl font-semibold text-center tabular-nums"
                      style={{
                        ...inputStyle,
                        maxWidth: '120px',
                        padding: '10px 14px',
                        borderRadius: '16px',
                        borderColor: V2.border,
                        color: V2.textDark,
                      }}
                      onFocus={inputFocusHandler}
                    />
                  </div>
                </div>

              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default EntryNewOne
